import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Zap, 
  ArrowLeft, 
  Save, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';
import { 
  getIntegrationById, 
  getIntegrationProvider, 
  createIntegration, 
  updateIntegration, 
  testIntegrationConnection,
  IntegrationProvider,
  Integration
} from '../lib/integrations';

const IntegrationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const preselectedProvider = queryParams.get('provider');

  const [integration, setIntegration] = useState<Integration | null>(null);
  const [provider, setProvider] = useState<IntegrationProvider | undefined>(undefined);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  const isEditMode = !!id;

  useEffect(() => {
    if (isEditMode) {
      fetchIntegration();
    } else if (preselectedProvider) {
      const providerDetails = getIntegrationProvider(preselectedProvider);
      if (providerDetails) {
        setProvider(providerDetails);
        setName(`${providerDetails.name} Integration`);
        
        // Initialize form data with empty values
        const initialData: Record<string, any> = {};
        providerDetails.configFields.forEach(field => {
          initialData[field.name] = '';
        });
        setFormData(initialData);
      }
    }
  }, [id, preselectedProvider]);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await getIntegrationById(id!);
      
      if (!data) {
        setError('Integration not found');
        return;
      }
      
      setIntegration(data);
      setName(data.name);
      setFormData(data.config || {});
      
      const providerDetails = getIntegrationProvider(data.provider);
      setProvider(providerDetails);
    } catch (err) {
      console.error('Error fetching integration:', err);
      setError('Failed to load integration');
    } finally {
      setLoading(false);
    }
  };

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const providerId = e.target.value;
    const providerDetails = getIntegrationProvider(providerId);
    
    if (providerDetails) {
      setProvider(providerDetails);
      setName(`${providerDetails.name} Integration`);
      
      // Initialize form data with empty values
      const initialData: Record<string, any> = {};
      providerDetails.configFields.forEach(field => {
        initialData[field.name] = field.type === 'checkbox' ? false : '';
      });
      setFormData(initialData);
    } else {
      setProvider(undefined);
      setFormData({});
    }
  };

  const handleInputChange = (fieldName: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const toggleShowSecret = (fieldName: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  const validateForm = (): boolean => {
    if (!provider) {
      setError('Please select an integration provider');
      return false;
    }
    
    if (!name.trim()) {
      setError('Integration name is required');
      return false;
    }
    
    // Check required fields
    for (const field of provider.configFields) {
      if (field.required && !formData[field.name]) {
        setError(`${field.label} is required`);
        return false;
      }
    }
    
    return true;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }
    
    try {
      setTestingConnection(true);
      setTestResult(null);
      setError(null);
      
      const result = await testIntegrationConnection(provider!.id, formData);
      setTestResult(result);
      
      if (!result.success) {
        setError(result.message);
      }
    } catch (err) {
      console.error('Error testing connection:', err);
      setError('Failed to test connection');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      if (isEditMode) {
        // Update existing integration
        const updatedIntegration = await updateIntegration(id!, {
          name,
          config: formData,
          status: testResult?.success ? 'active' : 'inactive'
        });
        
        if (updatedIntegration) {
          setSuccess('Integration updated successfully');
          setIntegration(updatedIntegration);
        } else {
          setError('Failed to update integration');
        }
      } else {
        // Create new integration
        const newIntegration = await createIntegration(
          provider!.id,
          name,
          formData
        );
        
        if (newIntegration) {
          setSuccess('Integration created successfully');
          
          // Redirect to the integrations list after a short delay
          setTimeout(() => {
            navigate('/integrations');
          }, 2000);
        } else {
          setError('Failed to create integration');
        }
      }
    } catch (err) {
      console.error('Error saving integration:', err);
      setError('Failed to save integration');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !provider) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading integration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/integrations')}
          className="p-2 bg-gray-800 rounded-lg text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">
            {isEditMode ? 'Edit Integration' : 'Add Integration'}
          </h1>
          <p className="text-gray-400 text-sm lg:text-base">
            {isEditMode 
              ? `Configure your ${integration?.name || 'integration'} settings` 
              : 'Connect ADAPT with your favorite tools'}
          </p>
        </div>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="bg-red-900 border border-red-700 rounded-lg p-4 flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {success && (
        <div className="bg-green-900 border border-green-700 rounded-lg p-4 flex items-center">
          <CheckCircle className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" />
          <span className="text-green-300">{success}</span>
        </div>
      )}

      {/* Integration Form */}
      <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Provider Selection (only in create mode) */}
          {!isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Integration Provider
              </label>
              <select
                value={provider?.id || ''}
                onChange={handleProviderChange}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                disabled={!!preselectedProvider}
              >
                <option value="">Select a provider</option>
                {INTEGRATION_PROVIDERS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Integration Name */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Integration Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
              placeholder="Enter a name for this integration"
              required
            />
          </div>

          {/* Provider-specific configuration fields */}
          {provider && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white">Configuration</h3>
              
              {provider.configFields.map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  
                  {field.type === 'text' && (
                    <input
                      type="text"
                      value={formData[field.name] || ''}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                      placeholder={field.placeholder}
                      required={field.required}
                    />
                  )}
                  
                  {field.type === 'password' && (
                    <div className="relative">
                      <input
                        type={showSecrets[field.name] ? 'text' : 'password'}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                        className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none pr-10"
                        placeholder={field.placeholder}
                        required={field.required}
                      />
                      <button
                        type="button"
                        onClick={() => toggleShowSecret(field.name)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
                      >
                        {showSecrets[field.name] ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  )}
                  
                  {field.type === 'select' && field.options && (
                    <select
                      value={formData[field.name] || ''}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                      required={field.required}
                    >
                      <option value="">Select an option</option>
                      {field.options.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {field.type === 'checkbox' && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id={field.name}
                        checked={!!formData[field.name]}
                        onChange={(e) => handleInputChange(field.name, e.target.checked)}
                        className="w-4 h-4 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500 focus:ring-offset-gray-800"
                      />
                      <label htmlFor={field.name} className="ml-2 text-sm text-gray-300">
                        {field.label}
                      </label>
                    </div>
                  )}
                  
                  {field.helpText && (
                    <p className="mt-1 text-xs text-gray-500">{field.helpText}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Test Connection Result */}
          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success 
                ? 'bg-green-900 border-green-700 text-green-300' 
                : 'bg-red-900 border-red-700 text-red-300'
            }`}>
              <div className="flex items-center space-x-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <XCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{testResult.message}</span>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4 border-t border-gray-700">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testingConnection || !provider}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testingConnection ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Testing...</span>
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  <span>Test Connection</span>
                </>
              )}
            </button>
            
            <button
              type="submit"
              disabled={loading || !provider}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>{isEditMode ? 'Update Integration' : 'Create Integration'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Integration Details */}
      {provider && (
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h3 className="text-lg font-medium text-white mb-4">About {provider.name}</h3>
          <p className="text-gray-400 mb-4">{provider.description}</p>
          
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-300 mb-2">Capabilities</h4>
            <ul className="space-y-2">
              {provider.capabilities.map((capability, index) => (
                <li key={index} className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">{capability}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-2">Category</h4>
            <div className="inline-block px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm capitalize">
              {provider.category.replace('-', ' ')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationForm;